import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, map, retry, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import {
  CreateSupportTicketPayload,
  SupportTicket,
  SupportTicketAttachment,
  SupportTicketListResponse,
  SupportTicketQueryParams,
  SupportTicketStatus,
} from 'src/app/models/support-ticket.model';

@Injectable({
  providedIn: 'root',
})
export class SupportTicketService {
  private readonly server = environment.eeTaskServer;
  private readonly ticketsUrl = `${this.server}/support-tickets`;
  private readonly uploadUrl = `${this.ticketsUrl}/upload`;
  private readonly openTicketCountSubject = new BehaviorSubject<number>(0);

  readonly openTicketCount$ = this.openTicketCountSubject.asObservable();

  constructor(
    private readonly http: HttpClient,
    private readonly apiService: ApiService,
    private readonly authService: AuthService
  ) {}

  listTickets(params: SupportTicketQueryParams = {}): Observable<SupportTicketListResponse> {
    const userId = this.getCurrentUserId();

    if (!userId) {
      return of(this.getEmptyTicketList(params));
    }

    return this.apiService
      .getSupportTicketsByUser(userId, {
        pageIndex: Math.max((params.page ?? 1) - 1, 0),
        pageSize: params.limit ?? 10,
        search: params.search,
        status: params.status && params.status !== 'All' ? String(params.status) : '',
      })
      .pipe(
        retry(1),
        map((response) => this.normalizeListResponse(response, params)),
        catchError(this.handleError)
      );
  }

  getTicket(ticketId: string): Observable<SupportTicket> {
    return this.apiService.getSupportTicketById(ticketId).pipe(
      retry(1),
      map((response) => {
        const apiResponse: any = response;
        return this.normalizeTicket(apiResponse?.data || apiResponse?.ticket || apiResponse?.result || apiResponse);
      }),
      catchError(this.handleError)
    );
  }

  createTicket(payload: CreateSupportTicketPayload): Observable<SupportTicket> {
    return this.http.post<any>(this.ticketsUrl, payload).pipe(
      retry(1),
      map((response) => this.normalizeTicket(response?.data || response?.ticket || response?.result || response)),
      tap(() => {
        void this.refreshOpenTicketCount().subscribe();
      }),
      catchError(this.handleError)
    );
  }

  submitTicket(userId: string, description: string, pdfFile: File | null, issue?: string): Observable<SupportTicket> {
    const formData = new FormData();
    formData.append('userId', userId);
    formData.append('description', description);

    if (pdfFile) {
      formData.append('pdfFile', pdfFile, pdfFile.name);
    }

    if (issue?.trim()) {
      formData.append('title', issue.trim());
    }

    return this.apiService.createSupportTicket(formData).pipe(
      retry(1),
      map((response: any) => {
        const apiResponse: any = response;
        const normalizedTicket = this.normalizeTicket(
          apiResponse?.data || apiResponse?.ticket || apiResponse?.result || apiResponse
        );

        return {
          ...normalizedTicket,
          issue: normalizedTicket.issue && normalizedTicket.issue !== '-' ? normalizedTicket.issue : issue?.trim() || '-',
          description: normalizedTicket.description && normalizedTicket.description !== '-' ? normalizedTicket.description : description,
        };
      }),
      tap(() => {
        void this.refreshOpenTicketCount().subscribe();
      }),
      catchError(this.handleError)
    );
  }

  submitUserReply(
    ticketId: string,
    message: string,
    pdfFile: File | null,
    closeTicket = false
  ): Observable<SupportTicket> {
    const formData = new FormData();
    formData.append('message', message);
    formData.append('closeTicket', String(closeTicket));

    if (pdfFile) {
      formData.append('pdfFile', pdfFile, pdfFile.name);
    }

    return this.apiService.submitSupportTicketUserReply(ticketId, formData).pipe(
      retry(1),
      map((response: any) => {
        const apiResponse: any = response;
        return this.normalizeTicket(apiResponse?.data || apiResponse?.ticket || apiResponse?.result || apiResponse);
      }),
      catchError(this.handleError)
    );
  }

  uploadAttachment(formData: FormData): Observable<any> {
    return this.http.post<any>(this.uploadUrl, formData).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  refreshOpenTicketCount(): Observable<number> {
    return this.listTickets({ page: 1, limit: 100 }).pipe(
      map((response) =>
        response.items.filter((ticket) => ticket.status !== SupportTicketStatus.Completed).length
      ),
      tap((count) => this.openTicketCountSubject.next(count)),
      catchError(() => {
        this.openTicketCountSubject.next(0);
        return of(0);
      })
    );
  }

  private normalizeListResponse(
    response: any,
    params: SupportTicketQueryParams
  ): SupportTicketListResponse {
    const payload = response?.data || response?.result || response || {};
    const itemsSource = payload?.tickets || payload?.items || payload?.results || [];
    const items = (Array.isArray(itemsSource) ? itemsSource : []).map((ticket) =>
      this.normalizeTicket(ticket)
    );
    const page = payload?.pageIndex !== undefined && payload?.pageIndex !== null
      ? Number(payload.pageIndex) + 1
      : Number(payload?.page || params.page || 1);
    const limit = Number(payload?.pageSize ?? payload?.limit ?? params.limit ?? items.length ?? 10);
    const total = Number(payload?.totalCount ?? payload?.total ?? payload?.count ?? items.length ?? 0);
    const hasMore = payload?.hasMore ?? page * limit < total;

    return {
      items,
      total,
      page,
      limit,
      hasMore,
    };
  }

  private normalizeTicket(ticket: any): SupportTicket {
    const attachment = this.normalizeAttachment(ticket?.pdf_file);
    const requester = ticket?.user_id || null;
    const conversation = Array.isArray(ticket?.conversation)
      ? ticket.conversation.map((item: any) => ({
          id: String(item?._id || `${item?.sender_id || item?.sender_type || 'message'}-${item?.created_at || Date.now()}`),
          senderType: item?.sender_type || 'user',
          senderId: item?.sender_id || '',
          message: item?.message || '',
          createdAt: item?.created_at || null,
          attachment: this.normalizeAttachment(item?.pdf_file),
        }))
      : [];

    return {
      id: String(ticket?.id || ticket?._id || ''),
      ticketNo: ticket?.ticketNo || ticket?.ticket_no || ticket?.ticketNumber || ticket?.id || ticket?._id || '-',
      title: ticket?.title || '-',
      description: ticket?.description || ticket?.issueDescription || '-',
      status: ticket?.status || SupportTicketStatus.Received,
      expectedCompletionDate:
        ticket?.expected_completion_date || null,
      createdOn: ticket?.createdAt || null,
      createdAt: ticket?.createdAt || null,
      completedAt: ticket?.completed_at || null,
      requesterName: requester?.name || '',
      requesterEmail: requester?.email || '',
      customerName: requester?.name || '',
      lastAdminReply: ticket?.last_admin_reply || '',
      conversation,
      attachment,
    };
  }

  private normalizeAttachment(attachment: any): SupportTicketAttachment | null {
    if (!attachment) {
      return null;
    }

    const directUrl =
      attachment?.downloadUrl ||
      null;

    return {
      id: attachment?.s3Key || attachment?._id || attachment?.id,
      fileName: attachment?.filename || attachment?.fileName || attachment?.name,
      name: attachment?.filename || attachment?.name || attachment?.fileName,
      url: directUrl,
      fileUrl: directUrl,
      downloadUrl: directUrl,
      path: attachment?.s3Key || attachment?.path || null,
      size: attachment?.size,
      uploadedAt: attachment?.uploadedAt || null,
    };
  }

  private getCurrentUserId(): string | null {
    const userId = this.authService.userValue?.id || this.authService.getCurrentUser()?.id;
    return userId ? String(userId) : null;
  }

  private handleError = (error: any) => {
    return throwError(() => error);
  };

  private getEmptyTicketList(params: SupportTicketQueryParams): SupportTicketListResponse {
    return {
      items: [],
      total: 0,
      page: Number(params.page ?? 1),
      limit: Number(params.limit ?? 10),
      hasMore: false,
    };
  }

  private cloneTicket(ticket: SupportTicket): SupportTicket {
    return {
      ...ticket,
      attachment: ticket.attachment ? { ...ticket.attachment } : null,
      conversation: ticket.conversation ? ticket.conversation.map((item) => ({ ...item })) : [],
    };
  }
}